/**
 * PersonaTargetsPanel (v2.5 SS-03 / PR C2, migration 140 — G0 §5).
 *
 * The domain's curated persona target population — the DENOMINATOR of every
 * Perceived coverage percentage. Renders the three status groups
 * (unreviewed / confirmed / not relevant), the reconciliation invariant
 * (confirmed + unreviewed + not_relevant = total mapped — asserted at
 * render time and after every transition), and the G0 §5.2 coverage-basis
 * summary.
 *
 * Mutations are ADMIN-ONLY (server-enforced; buttons are a courtesy) and
 * audited per persona (`persona_target_status_changed` with {from, to}).
 * "Not relevant" is a recorded decision, never a row deletion. Bulk
 * confirm uses the G0 "Confirm the target population?" dialog with the
 * audit disclosure. Announcements are aria-live strings — no toasts.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../contexts/AuthContext';
import type { PersonaTarget, PersonaTargetCounts, PersonaTargetStatus } from '../../types/triangulation';

/**
 * The G0 "Confirm the target population?" dialog — a REAL modal per the
 * approved B2 dialog contract (WorkflowDeclarationDialog): backdrop
 * isolation, heading-first focus, Tab trap, Escape cancels, and the CALLER
 * restores focus to the trigger on close (round-2 P1).
 */
const ConfirmAllDialog: React.FC<{
  unreviewedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ unreviewedCount, onConfirm, onCancel }) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Initial focus: the NAMED HEADING (G0) — the dialog's purpose is
  // announced before any control.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const titleId = 'pt-dlg-title';
  return (
    <div className="tri-dialog-backdrop" onKeyDown={onKeyDown} data-testid="confirm-all-backdrop">
      <div
        className="tri-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <h3 id={titleId} tabIndex={-1} ref={headingRef}>
          Confirm the target population?
        </h3>
        <p>
          Every unreviewed persona ({unreviewedCount}) becomes a confirmed target — the
          denominator of this domain&apos;s Perceived coverage.
        </p>
        <p className="tri-quiet-note">
          Recorded as persona_target_status_changed per persona, with your name and role.
        </p>
        <div className="tri-dialog-actions">
          <button type="button" className="tri-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="tri-btn tri-btn--accent"
            onClick={onConfirm}
            data-testid="confirm-all-commit"
          >
            Confirm all
          </button>
        </div>
      </div>
    </div>
  );
};

interface PersonaTargetsPanelProps {
  domainId: string;
  /** Called after any successful status change so the parent view can
   *  re-fetch the triangulation payload (coverage moved). */
  onTargetsChanged?: () => void;
}

interface TargetsPayload {
  targets: PersonaTarget[];
  counts: PersonaTargetCounts;
}

type PanelState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: TargetsPayload };

function coverageBasisLabel(counts: PersonaTargetCounts): string {
  // Round-3 P1: a zero-target population is UNSET — the server classifies
  // it as unreviewed with no denominator, and this label must never claim
  // "Confirmed targets" over empty counts (the CoverageChip renders the
  // same unset state).
  if (counts.total === 0) return 'Not set';
  if (counts.unreviewed > 0) return 'Unreviewed';
  return 'Confirmed targets';
}

export const PersonaTargetsPanel: React.FC<PersonaTargetsPanelProps> = ({
  domainId,
  onTargetsChanged,
}) => {
  const { authFetch } = useApiClient();
  const { user } = useAuth();
  const isAdmin = (user?.role ?? 'viewer') === 'admin';

  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [announcement, setAnnouncement] = useState<string>('');
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Round-2/3 P1 focus contract: cancel and FAILURE restore focus to the
  // trigger (still mounted — unreviewed targets remain); a SUCCESSFUL
  // confirm removes the trigger (zero unreviewed), so focus moves to the
  // stable reconciliation summary instead of falling to document.body.
  const confirmAllTriggerRef = useRef<HTMLButtonElement | null>(null);
  const reconciliationRef = useRef<HTMLDivElement | null>(null);
  // Focus restoration is DEFERRED to after the busy re-render: while busy
  // the trigger is disabled and refuses focus.
  const [pendingFocus, setPendingFocus] = useState<'trigger' | 'summary' | null>(null);
  useEffect(() => {
    if (busy || !pendingFocus) return;
    if (pendingFocus === 'trigger' && confirmAllTriggerRef.current) {
      confirmAllTriggerRef.current.focus();
    } else {
      // Trigger unmounted (zero unreviewed) or summary requested — the
      // reconciliation line is the stable landing point.
      reconciliationRef.current?.focus?.();
    }
    setPendingFocus(null);
  }, [busy, pendingFocus]);
  const closeConfirmAll = (): void => {
    setConfirmAllOpen(false);
    confirmAllTriggerRef.current?.focus?.();
  };

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await authFetch(`/api/domains/${domainId}/persona-targets`);
      if (!res.ok) throw new Error(`Failed to load persona targets (${res.status})`);
      const data = (await res.json()) as Partial<TargetsPayload>;
      if (!Array.isArray(data.targets) || !data.counts) {
        throw new Error('Malformed persona-targets payload.');
      }
      setState({ kind: 'ready', data: data as TargetsPayload });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, [authFetch, domainId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (target: PersonaTarget, status: PersonaTargetStatus): Promise<void> => {
    setBusy(true);
    try {
      const res = await authFetch(
        `/api/domains/${domainId}/persona-targets/${target.persona_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        },
      );
      if (!res.ok) throw new Error(`Status change failed (${res.status})`);
      setAnnouncement(
        `${target.persona_name} marked ${status.replace('_', ' ')}. Recorded as persona_target_status_changed.`,
      );
      await load();
      onTargetsChanged?.();
    } catch (err: unknown) {
      setAnnouncement(
        `Could not change ${target.persona_name}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmAll = async (): Promise<void> => {
    setBusy(true);
    setConfirmAllOpen(false);
    try {
      const res = await authFetch(`/api/domains/${domainId}/persona-targets/confirm-all`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Bulk confirm failed (${res.status})`);
      const body = (await res.json()) as { confirmed: number };
      setAnnouncement(
        `${body.confirmed} persona${body.confirmed === 1 ? '' : 's'} confirmed as the target population. Recorded as persona_target_status_changed per persona.`,
      );
      await load();
      onTargetsChanged?.();
      // Success removes the trigger (zero unreviewed) — land focus on the
      // stable reconciliation summary, never document.body.
      setPendingFocus('summary');
    } catch (err: unknown) {
      setAnnouncement(
        `Bulk confirm failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      // Failure keeps the trigger mounted — restore focus there (deferred
      // past the disabled-while-busy render).
      setPendingFocus('trigger');
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === 'loading') {
    return (
      <section className="tri-targets" data-testid="persona-targets" aria-label="Persona target population">
        <div className="tri-status" role="status">Loading persona targets…</div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="tri-targets" data-testid="persona-targets" aria-label="Persona target population">
        <div className="tri-alert" role="alert">
          <p>{state.message}</p>
          <button type="button" className="tri-btn" onClick={() => void load()}>Retry</button>
        </div>
      </section>
    );
  }

  const { targets, counts } = state.data;
  const unreviewed = targets.filter((t) => t.status === 'unreviewed');
  const confirmed = targets.filter((t) => t.status === 'confirmed');
  const notRelevant = targets.filter((t) => t.status === 'not_relevant');
  // G0 §5.1 reconciliation invariant — asserted at render time.
  const reconciles =
    counts.confirmed + counts.unreviewed + counts.not_relevant === counts.total &&
    confirmed.length === counts.confirmed &&
    unreviewed.length === counts.unreviewed &&
    notRelevant.length === counts.not_relevant;

  return (
    <section className="tri-targets" data-testid="persona-targets" aria-label="Persona target population">
      <div className="tri-section-label">Persona target population</div>
      <p className="tri-targets-intro">
        Coverage is a fraction, and a fraction is only as honest as its denominator. Seeded
        personas arrive <em>unreviewed</em> — a proposal, not a target population. Until an
        administrator reviews them, coverage renders as preliminary and never as sufficient.
      </p>

      <div
        className={`tri-targets-assert ${reconciles ? '' : 'tri-targets-assert--fail'}`}
        role="status"
        data-testid="targets-reconciliation"
        tabIndex={-1}
        ref={reconciliationRef}
      >
        {reconciles
          ? `Reconciled: ${counts.confirmed} confirmed + ${counts.unreviewed} unreviewed + ${counts.not_relevant} not relevant = ${counts.total} mapped.`
          : 'Reconciliation FAILED — status counts do not sum to the mapped total. Reload before acting.'}
      </div>

      <dl className="tri-targets-stats" data-testid="targets-counts">
        <div><dt>Confirmed</dt><dd>{counts.confirmed}</dd></div>
        <div><dt>Unreviewed</dt><dd>{counts.unreviewed}</dd></div>
        <div><dt>Not relevant</dt><dd>{counts.not_relevant}</dd></div>
        <div><dt>Coverage basis</dt><dd>{coverageBasisLabel(counts)}</dd></div>
      </dl>

      {isAdmin && counts.unreviewed > 0 && (
        <div className="tri-targets-actions">
          <button
            type="button"
            className="tri-btn tri-btn--accent"
            disabled={busy}
            onClick={() => setConfirmAllOpen(true)}
            data-testid="confirm-all-open"
            ref={confirmAllTriggerRef}
          >
            Confirm all as target population
          </button>
          <span className="tri-quiet-note">Admin only · audited per persona</span>
        </div>
      )}

      {confirmAllOpen && (
        <ConfirmAllDialog
          unreviewedCount={counts.unreviewed}
          onCancel={closeConfirmAll}
          onConfirm={() => void confirmAll()}
        />
      )}

      <TargetGroup
        title={`Unreviewed — ${unreviewed.length}`}
        caption="Unreviewed persona targets — proposed by the seed catalog."
        targets={unreviewed}
        emptyText="No unreviewed personas — the population is fully reviewed."
        renderActions={(t) =>
          isAdmin ? (
            <>
              <button type="button" className="tri-btn" disabled={busy} onClick={() => void setStatus(t, 'confirmed')}>
                Confirm as target
              </button>
              <button type="button" className="tri-btn" disabled={busy} onClick={() => void setStatus(t, 'not_relevant')}>
                Mark not relevant
              </button>
            </>
          ) : null
        }
      />

      <TargetGroup
        title={`Confirmed target population — ${confirmed.length}`}
        caption="Confirmed persona targets. These are the denominator for Perceived coverage."
        targets={confirmed}
        emptyText="No confirmed targets yet."
        renderActions={(t) =>
          isAdmin ? (
            <>
              <button type="button" className="tri-btn" disabled={busy} onClick={() => void setStatus(t, 'unreviewed')}>
                Return to unreviewed
              </button>
              <button type="button" className="tri-btn" disabled={busy} onClick={() => void setStatus(t, 'not_relevant')}>
                Mark not relevant
              </button>
            </>
          ) : null
        }
      />

      <TargetGroup
        title={`Not relevant — ${notRelevant.length}`}
        caption="Personas excluded from the target population. Reversible and audited — never a deletion."
        targets={notRelevant}
        emptyText="No personas marked not relevant."
        showReason
        renderActions={(t) =>
          isAdmin ? (
            <button type="button" className="tri-btn" disabled={busy} onClick={() => void setStatus(t, 'unreviewed')}>
              Restore to unreviewed
            </button>
          ) : null
        }
      />

      <p className="tri-targets-floor">
        Coverage reads Sufficient only when at least 3 personas have contributed AND at least
        50% of the confirmed targets are represented. With fewer than 3 contributors the
        percentage means too little to publish, and the chip says so rather than rounding up.
      </p>

      {/* No toasts (G0) — announcements are polite live-region strings. */}
      <div aria-live="polite" className="tri-visually-hidden" data-testid="targets-announcement">
        {announcement}
      </div>
    </section>
  );
};

interface TargetGroupProps {
  title: string;
  caption: string;
  targets: PersonaTarget[];
  emptyText: string;
  showReason?: boolean;
  renderActions: (t: PersonaTarget) => React.ReactNode;
}

const TargetGroup: React.FC<TargetGroupProps> = ({
  title,
  caption,
  targets,
  emptyText,
  showReason = false,
  renderActions,
}) => (
  <div className="tri-targets-group">
    <h3>{title}</h3>
    {targets.length === 0 ? (
      <p className="tri-quiet-note">{emptyText}</p>
    ) : (
      <table className="tri-targets-table">
        <caption className="tri-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Persona</th>
            <th scope="col">Source</th>
            <th scope="col">Status set</th>
            {showReason && <th scope="col">Reason</th>}
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((t) => (
            <tr key={t.persona_id} data-testid={`target-${t.persona_id}`} data-status={t.status}>
              <td>
                {t.persona_name}
                {t.persona_archetype && <span className="tri-quiet-note"> · {t.persona_archetype}</span>}
              </td>
              <td>{t.source === 'seed' ? 'Seed catalog' : 'Added by admin'}</td>
              <td>
                {t.status_set_at
                  ? new Date(t.status_set_at).toLocaleDateString()
                  : '—'}
              </td>
              {showReason && <td>{t.status_reason ?? '—'}</td>}
              <td className="tri-targets-rowactions">{renderActions(t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
